/**
 * The one place the SPA turns a timestamp into text.
 *
 * Every time on the dashboard is shown in the *application* timezone and says so
 * ("5:00 PM PKT"), never in whatever zone the viewer's browser happens to be set to:
 * two analysts must read the same moment identically. The zone is served by the
 * backend (`/meta/config` → `boond_contracts.tz`, the single timezone authority) and
 * hydrated here, exactly as `lib/thresholds.ts` is. The literals below are only the
 * boot-time fallback before the config response, and mirror the backend default.
 *
 * Two shapes arrive from the API and must not be confused:
 *  - with an offset (`…+05:00`, `…Z`): a real instant — converted into the app zone;
 *  - without one (`2026-04-01T12:00:00`): a naive wall-clock that the backend already
 *    stores in the app zone (CLAUDE.md rule #2) — shown as written. `new Date()` would
 *    read it in the *browser's* zone, so it is never used on a naive string.
 *
 * A calendar day (`YYYY-MM-DD`, the API's "as of" days) is not an instant: it has no
 * zone to convert and is formatted as written.
 */

export interface TimeConfig {
  timezone: string;
  timezone_label: string;
}

const FALLBACK: TimeConfig = { timezone: "Asia/Karachi", timezone_label: "PKT" };
let current: TimeConfig = FALLBACK;

/** Called once by the config bootstrap with the server's values. */
export function hydrateTime(next: Partial<TimeConfig> | undefined): void {
  if (next?.timezone) current = { ...FALLBACK, ...next };
}

/** The application timezone's IANA name. Read at call time. */
export function appTimezone(): string {
  return current.timezone;
}

/** The application timezone's short display name (e.g. `PKT`). */
export function appTimezoneLabel(): string {
  return current.timezone_label;
}

// `YYYY-MM-DD`, optionally `[T ]HH:MM[:SS[.fff]]`, with an optional `Z` / `±HH[:MM]`.
const ISO =
  /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?)?\s*(Z|[+-]\d{2}(?::?\d{2})?)?$/;

interface Parsed {
  /** Wall-clock fields as written, stamped onto UTC so they can be formatted as-is. */
  wallUtcMs: number;
  /** The real instant, or `null` for a naive string (resolved by `toEpochMs`). */
  instantMs: number | null;
  hasTime: boolean;
}

function parse(iso: string): Parsed | null {
  const m = ISO.exec(iso.trim());
  if (!m) return null;
  const [, y, mo, d, h, mi, s, zone] = m;
  const wallUtcMs = Date.UTC(+y, +mo - 1, +d, +(h ?? 0), +(mi ?? 0), +(s ?? 0));
  if (Number.isNaN(wallUtcMs)) return null;
  let instantMs: number | null = null;
  if (zone) {
    const t = Date.parse(iso.trim().replace(" ", "T"));
    if (Number.isNaN(t)) return null;
    instantMs = t;
  }
  return { wallUtcMs, instantMs, hasTime: h !== undefined };
}

/** The zone's offset from UTC at an instant, in ms (positive east of Greenwich). */
function offsetMs(atMs: number, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
  }).formatToParts(new Date(atMs));
  const n = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  const asUtc = Date.UTC(
    n("year"),
    n("month") - 1,
    n("day"),
    n("hour"),
    n("minute"),
    n("second"),
  );
  return asUtc - Math.floor(atMs / 1000) * 1000;
}

/**
 * The epoch milliseconds a timestamp denotes, for arithmetic ("3 h ago"). A naive
 * wall-clock is read as app-zone time; returns `NaN` for anything unparseable.
 */
export function toEpochMs(iso: string | null | undefined): number {
  const p = iso ? parse(iso) : null;
  if (!p) return Number.NaN;
  if (p.instantMs !== null) return p.instantMs;
  return p.wallUtcMs - offsetMs(p.wallUtcMs, current.timezone);
}

function format(
  iso: string | null | undefined,
  lang: string,
  options: Intl.DateTimeFormatOptions,
): string | null {
  const p = iso ? parse(iso) : null;
  if (!p) return null;
  // A real instant is converted into the app zone; a naive wall-clock is already in
  // it, so its fields are formatted verbatim (as UTC, which converts nothing).
  const [at, timeZone] =
    p.instantMs !== null ? [p.instantMs, current.timezone] : [p.wallUtcMs, "UTC"];
  return new Intl.DateTimeFormat(lang, { ...options, timeZone }).format(new Date(at));
}

/** Absolute date and time with the zone named, or `null` when there is nothing to show. */
export function formatWhen(
  iso: string | null | undefined,
  lang: string,
): string | null {
  const text = format(iso, lang, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
  return text === null ? null : `${text} ${current.timezone_label}`;
}

/** Date only — for columns where the time of day is noise. `"—"` when absent. */
export function formatDate(iso: string | null | undefined, lang: string): string {
  if (!iso) return "—";
  return (
    format(iso, lang, { year: "numeric", month: "numeric", day: "numeric" }) ?? iso
  );
}

/**
 * A calendar day (`YYYY-MM-DD`, as the API states "as of" days). No zone is applied:
 * it names a day, not a moment — and parsing it through `new Date("2026-09-29")` would
 * read UTC midnight and render 28 September anywhere west of Greenwich.
 */
export function formatDay(isoDay: string | null | undefined, lang: string): string {
  if (!isoDay) return "—";
  return formatDate(`${isoDay.slice(0, 10)}T00:00:00`, lang);
}

/** Today's calendar day (`YYYY-MM-DD`) in the application zone, not the browser's. */
export function todayInAppZone(now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: current.timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const v = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${v("year")}-${v("month")}-${v("day")}`;
}
